import { useContext, useEffect, useRef, useState } from 'react';

import { IntersectionObserverContext } from './IntersectionObserverContext';

export const useIntersectionObserver = <T extends HTMLElement | HTMLElement[]>(
  elementRef: React.RefObject<T>,
  options: IntersectionObserverInit = {},
) => {
  const context = useContext(IntersectionObserverContext);
  const isObserved = useRef<boolean>(false);

  if (context === undefined) throw new Error('error');

  const { observe: _observe, unobserve: _unobserve, entries } = context;
  const [entry, setEntry] = useState<IntersectionObserverEntry | IntersectionObserverEntry[]>();

  const observe = () => {
    if (typeof window === 'undefined' || isObserved.current) return;

    if (Array.isArray(elementRef.current)) {
      for (const item of elementRef.current) {
        if (item) _observe(item, options);
      }
    } else if (elementRef.current) {
      _observe(elementRef.current, options);
    }

    isObserved.current = true;
  };

  const unobserve = () => {
    if (typeof window === 'undefined') return;

    if (Array.isArray(elementRef.current)) {
      for (const item of elementRef.current) {
        if (item) _unobserve(item, options);
      }
    } else if (elementRef.current) {
      _unobserve(elementRef.current, options);
    }

    isObserved.current = false;
  };

  useEffect(() => unobserve, []);

  useEffect(() => {
    if (Array.isArray(elementRef.current)) {
      const result: IntersectionObserverEntry[] = [];
      for (const item of elementRef.current) {
        result.push(entries.get(item) as IntersectionObserverEntry);
      }
      setEntry(result);
    } else if (elementRef.current) {
      setEntry(entries.get(elementRef.current));
    }
  }, [entries, elementRef]);

  return {
    entry: entry as T extends HTMLElement ? IntersectionObserverEntry : IntersectionObserverEntry[] | undefined,
    observe,
    unobserve,
  };
};
